import type { EncounterDefinition, EnemyDefinition } from '../core/types';

export const enemyDefinitions: EnemyDefinition[] = [
  // Act I — Bellrose Extraction Line.
  {
    id: 'tunnel_mite',
    name: "Bellrose Burrower",
    maxHp: 24,
    description: "A Bellrose burrower distorted into a plated Faunal Rendling. It has found warmth beneath the dry transfer rails.",
    pattern: [
      { kind: 'attack', amount: 5 },
      { kind: 'block', amount: 6 },
      { kind: 'attack', amount: 8 },
    ],
  },
  {
    id: 'chain_scavenger',
    name: "Tether Deserter",
    maxHp: 31,
    description: "A former extraction hand keeps the camp's last tether for himself. He fears the evacuation order is a lie.",
    pattern: [
      { kind: 'attack', amount: 7 },
      { kind: 'buff', status: 'strength', amount: 2 },
      { kind: 'attack', amount: 9 },
    ],
  },
  {
    id: 'ore_tick',
    name: "Glassback Grazer",
    maxHp: 18,
    description: "A once-timid island grazer, now a glass-plated Faunal Rendling. Its splintered mouthparts tear through field dressings.",
    pattern: [
      { kind: 'attack_status', amount: 4, status: 'weak', statusAmount: 1 },
      { kind: 'attack', amount: 6, hits: 2 },
    ],
  },
  {
    id: 'dustlung_sapper',
    name: "Breach Sapper",
    maxHp: 28,
    description: "A panicked demolition worker is opening the containment fence with dry charges. The dust masks every escape route.",
    pattern: [
      { kind: 'block', amount: 7 },
      {
        kind: 'attack_status',
        amount: 6,
        status: 'vulnerable',
        statusAmount: 1,
        statusCard: { definitionId: 'choking_dust', amount: 1 },
      },
      { kind: 'attack', amount: 10 },
    ],
  },
  {
    id: 'gallery_enforcer',
    name: "Quarantine Enforcer",
    maxHp: 58,
    description: "A quarantine officer in an armored field frame. The old order says nobody leaves; no new order has arrived.",
    pattern: [
      { kind: 'block', amount: 10 },
      { kind: 'attack', amount: 12 },
      { kind: 'attack_status', amount: 8, status: 'vulnerable', statusAmount: 2 },
      { kind: 'buff', status: 'strength', amount: 2 },
      { kind: 'attack', amount: 18 },
    ],
  },
  {
    id: 'pit_foreman',
    name: "The Lockdown Foreman",
    maxHp: 100,
    description: "The camp overseer has locked himself into a heavy extraction frame. He will not release the gantry until the missing consignment is accounted for.",
    pattern: [
      { kind: 'attack', amount: 7, hits: 2 },
      { kind: 'block', amount: 12 },
      { kind: 'attack_status', amount: 9, status: 'weak', statusAmount: 1 },
      { kind: 'attack', amount: 14 },
    ],
    phases: [
      {
        atHp: 50,
        onEnterBlock: 12,
        onEnterStrength: 1,
        pattern: [
          { kind: 'attack', amount: 7, hits: 3 },
          { kind: 'block', amount: 18 },
          {
            kind: 'attack_status',
            amount: 13,
            status: 'vulnerable',
            statusAmount: 2,
            statusCard: { definitionId: 'choking_dust', amount: 2 },
          },
          { kind: 'attack', amount: 9, hits: 3 },
        ],
      },
    ],
  },

  // Act II — Sealed Research Station.
  {
    id: 'ember_mendicant',
    name: "Spent Catalurgist",
    maxHp: 38,
    description: "A Genesis practitioner past exhaustion, still sustaining the station's emergency barriers. The cooling garment is failing.",
    pattern: [
      { kind: 'buff', status: 'strength', amount: 1 },
      { kind: 'attack', amount: 6, hits: 2, playerHeat: 1 },
      { kind: 'block', amount: 8 },
      {
        kind: 'attack_status',
        amount: 9,
        status: 'weak',
        statusAmount: 1,
        statusCard: { definitionId: 'slag_spark', amount: 1 },
      },
    ],
  },
  {
    id: 'slag_hound',
    name: "Containment Hound",
    maxHp: 34,
    description: "A low-slung Faunal Rendling escaped from a specimen pen. The restraint collar no longer holds its jaw.",
    pattern: [
      { kind: 'attack', amount: 7 },
      { kind: 'attack', amount: 4, hits: 2 },
      { kind: 'attack_status', amount: 8, status: 'vulnerable', statusAmount: 1 },
    ],
  },
  {
    id: 'kiln_bailiff',
    name: "Isolation Warden",
    maxHp: 48,
    description: "An isolation officer carries a shield cut from a bulkhead door. Behind it, the station's survivors still need protection.",
    pattern: [
      { kind: 'block', amount: 12 },
      { kind: 'attack', amount: 11 },
      { kind: 'attack_status', amount: 9, status: 'vulnerable', statusAmount: 2 },
      { kind: 'attack', amount: 14 },
    ],
  },
  {
    id: 'ash_cantor',
    name: "Signal Suppressor",
    maxHp: 40,
    description: "A security technician's sonic suppression rig makes every breath a struggle. Its damaged relay mistakes movement for a breach.",
    pattern: [
      { kind: 'attack_status', amount: 6, status: 'weak', statusAmount: 2 },
      { kind: 'block', amount: 7 },
      { kind: 'buff', status: 'strength', amount: 2 },
      { kind: 'attack', amount: 8, hits: 2 },
    ],
  },
  {
    id: 'crucible_penitent',
    name: "Overrun Test Frame",
    maxHp: 82,
    description: "A research operator trapped in an overdriven test frame. The restraints turn each attempt to escape into another strike.",
    pattern: [
      { kind: 'block', amount: 16 },
      { kind: 'buff', status: 'strength', amount: 2 },
      { kind: 'attack', amount: 11, hits: 2 },
      { kind: 'attack_status', amount: 14, status: 'vulnerable', statusAmount: 2 },
      { kind: 'attack', amount: 22 },
    ],
  },
  {
    id: 'bellows_saint',
    name: "The Refinery Custodian",
    maxHp: 145,
    description: "The refinery's last custodian is sustaining emergency Genesis baffles while fighting Burn. She cannot see a safe way to stop the damaged installation.",
    pattern: [
      { kind: 'attack', amount: 9, playerHeat: 1 },
      { kind: 'block', amount: 18 },
      { kind: 'attack', amount: 6, hits: 2, playerHeat: 1 },
      { kind: 'attack_status', amount: 10, status: 'weak', statusAmount: 1 },
    ],
    phases: [
      {
        atHp: 72,
        onEnterBlock: 16,
        pattern: [
          { kind: 'attack', amount: 7, hits: 2, playerHeat: 2 },
          { kind: 'block', amount: 22 },
          {
            kind: 'attack_status',
            amount: 13,
            status: 'vulnerable',
            statusAmount: 2,
            statusCard: { definitionId: 'slag_spark', amount: 1 },
          },
          { kind: 'attack', amount: 7, hits: 3, playerHeat: 1 },
        ],
      },
    ],
  },

  // Act III — Counterweight Vault; devices are adaptation inventions.
  {
    id: 'toolborn_mimic',
    name: "Unheld Tool",
    maxHp: 54,
    description: "A cable-driven manipulator keeps repeating an unfinished tool trial. There is no hand at its controls now.",
    pattern: [
      { kind: 'attack', amount: 10 },
      { kind: 'block', amount: 12 },
      {
        kind: 'attack_status',
        amount: 11,
        status: 'vulnerable',
        statusAmount: 1,
        statusCard: { definitionId: 'white_echo', amount: 1 },
      },
      { kind: 'attack', amount: 14 },
    ],
  },
  {
    id: 'marrow_loom',
    name: "Specimen Weaver",
    maxHp: 62,
    description: "A maintenance loom repairs ruptured specimen restraints with needle arms. Its damaged sensors cannot distinguish a person from a torn harness.",
    pattern: [
      { kind: 'block', amount: 14 },
      {
        kind: 'attack_status',
        amount: 8,
        status: 'weak',
        statusAmount: 2,
        statusCard: { definitionId: 'white_echo', amount: 1 },
      },
      { kind: 'buff', status: 'strength', amount: 2 },
      { kind: 'attack', amount: 12, hits: 2 },
    ],
  },
  {
    id: 'vein_shear',
    name: "Intake Shears",
    maxHp: 46,
    description: "A mobile intake cutter follows pressurized lines through the vault. Its worn guide can mistake your harness tubing for the next cut.",
    pattern: [
      { kind: 'attack', amount: 7, hits: 2 },
      {
        kind: 'attack_status',
        amount: 12,
        status: 'vulnerable',
        statusAmount: 1,
        gearWearOnHpDamage: 1,
      },
      { kind: 'attack', amount: 9, hits: 2 },
    ],
  },
  {
    id: 'quicksteel_echo',
    name: "Unstable Duplicate",
    maxHp: 50,
    description: "A reflective training mannequin dragged by a damaged return cable. Each repeated lunge winds its spring tighter.",
    pattern: [
      { kind: 'buff', status: 'strength', amount: 1 },
      { kind: 'attack', amount: 8 },
      { kind: 'block', amount: 10 },
      { kind: 'attack', amount: 6, hits: 3 },
    ],
  },
  {
    id: 'crownless_engine',
    name: "Counterweight Engine",
    maxHp: 108,
    description: "A suspended balancing machine sweeps the vault beneath its counterweights. Its mechanical governors have lost their reference.",
    pattern: [
      { kind: 'block', amount: 20 },
      { kind: 'attack', amount: 13, hits: 2 },
      { kind: 'buff', status: 'strength', amount: 3 },
      {
        kind: 'attack_status',
        amount: 18,
        status: 'vulnerable',
        statusAmount: 2,
        gearWearOnHpDamage: 1,
      },
      { kind: 'block', amount: 15 },
      { kind: 'attack', amount: 10, hits: 3 },
    ],
  },
  {
    id: 'hollow_heart',
    name: "The Counterfeit Heart",
    maxHp: 165,
    description: "An experimental intake engine built to imitate Dimiti's balance. It is neither a god nor his heart: steel, trapped pressure, and a shutdown nobody dared attempt.",
    pattern: [
      { kind: 'block', amount: 20 },
      { kind: 'attack', amount: 10, hits: 2 },
      { kind: 'attack_status', amount: 11, status: 'weak', statusAmount: 1 },
      { kind: 'attack', amount: 16 },
    ],
    phases: [
      {
        atHp: 109,
        onEnterBlock: 12,
        onEnterStrength: 1,
        pattern: [
          { kind: 'attack', amount: 8, hits: 3 },
          { kind: 'block', amount: 24 },
          {
            kind: 'attack',
            amount: 13,
            gearWearOnHpDamage: 1,
            statusCard: { definitionId: 'white_echo', amount: 1 },
          },
          { kind: 'attack_status', amount: 14, status: 'vulnerable', statusAmount: 2 },
        ],
      },
      {
        atHp: 54,
        onEnterBlock: 15,
        onEnterStrength: 1,
        pattern: [
          { kind: 'attack', amount: 7, hits: 3, gearWearOnHpDamage: 1 },
          { kind: 'block', amount: 28 },
          { kind: 'attack_status', amount: 16, status: 'vulnerable', statusAmount: 2 },
          {
            kind: 'attack',
            amount: 9,
            hits: 3,
            statusCard: { definitionId: 'white_echo', amount: 2 },
          },
        ],
      },
    ],
  },
];

export const encounterDefinitions: EncounterDefinition[] = [
  // Act I — five normal formations, one elite, one boss.
  {
    id: 'gallery_intro',
    name: "First Spill",
    act: 1,
    kind: 'normal',
    enemies: ['tunnel_mite'],
  },
  {
    id: 'gallery_vermin',
    name: "Under the Transfer Rails",
    act: 1,
    kind: 'normal',
    enemies: ['ore_tick', 'tunnel_mite'],
  },
  {
    id: 'gallery_ambush',
    name: "The Tether Toll",
    act: 1,
    kind: 'normal',
    enemies: ['chain_scavenger', 'ore_tick'],
  },
  {
    id: 'gallery_sapper',
    name: "Breach in the Fence",
    act: 1,
    kind: 'normal',
    enemies: ['dustlung_sapper'],
  },
  {
    id: 'gallery_scavengers',
    name: "Abandoned Consignment",
    act: 1,
    kind: 'normal',
    enemies: ['chain_scavenger', 'tunnel_mite'],
  },
  {
    id: 'gallery_enforcer',
    name: "Quarantine Order",
    act: 1,
    kind: 'elite',
    enemies: ['gallery_enforcer'],
  },
  {
    id: 'pit_foreman',
    name: "Lockdown at the Gantry",
    act: 1,
    kind: 'boss',
    enemies: ['pit_foreman'],
  },

  // Act II — five normal formations, one elite, one boss.
  {
    id: 'ember_first_sparks',
    name: "Teeth in the Transfer Bay",
    act: 2,
    kind: 'normal',
    enemies: ['slag_hound'],
  },
  {
    id: 'ember_procession',
    name: "The Exhausted Shift",
    act: 2,
    kind: 'normal',
    enemies: ['ember_mendicant', 'slag_hound'],
  },
  {
    id: 'ember_kiln_gate',
    name: "The Isolation Gate",
    act: 2,
    kind: 'normal',
    enemies: ['kiln_bailiff'],
  },
  {
    id: 'ember_ashen_choir',
    name: "Dead Signal",
    act: 2,
    kind: 'normal',
    enemies: ['ash_cantor', 'ember_mendicant'],
  },
  {
    id: 'ember_hound_pack',
    name: "Containment Kennels",
    act: 2,
    kind: 'normal',
    enemies: ['slag_hound', 'slag_hound'],
  },
  {
    id: 'ember_crucible',
    name: "A Failed Field Trial",
    act: 2,
    kind: 'elite',
    enemies: ['crucible_penitent'],
  },
  {
    id: 'bellows_saint',
    name: "The Sealed Refinery",
    act: 2,
    kind: 'boss',
    enemies: ['bellows_saint'],
  },

  // Act III — five normal formations, one elite, one boss.
  {
    id: 'foundry_first_cast',
    name: "The Unheld Tool",
    act: 3,
    kind: 'normal',
    enemies: ['toolborn_mimic'],
  },
  {
    id: 'foundry_cutting_floor',
    name: "Intake Cutting Floor",
    act: 3,
    kind: 'normal',
    enemies: ['vein_shear', 'quicksteel_echo'],
  },
  {
    id: 'foundry_bone_shift',
    name: "The Specimen Loom",
    act: 3,
    kind: 'normal',
    enemies: ['marrow_loom'],
  },
  {
    id: 'foundry_false_armory',
    name: "Manufactured Defenders",
    act: 3,
    kind: 'normal',
    enemies: ['toolborn_mimic', 'vein_shear'],
  },
  {
    id: 'foundry_repetition',
    name: "An Unstable Repetition",
    act: 3,
    kind: 'normal',
    enemies: ['marrow_loom', 'quicksteel_echo'],
  },
  {
    id: 'foundry_crownless',
    name: "Counterweight Trial",
    act: 3,
    kind: 'elite',
    enemies: ['crownless_engine'],
  },
  {
    id: 'hollow_heart',
    name: "The Last Intake",
    act: 3,
    kind: 'boss',
    enemies: ['hollow_heart'],
  },
];
